// Wi-Fi credentials, typed and sent over the session that is already open.
//
// WHY NOT provisionWifiOverBle. That helper opens its OWN session, provisions,
// and closes — right for onboarding, wrong here. On the bench the link is
// already up and authenticated, and tearing it down to make a second one throws
// away the state the operator just established and hides the write inside a
// helper. Here the write goes out on the open link and lands in the exchange log
// with everything else, which is the reason to be on this screen at all.
//
// The PAYLOAD choice is not reimplemented — chooseWifiPayload owns it, because
// which of the two forms survives depends on the characters in the credentials
// (`WIFI:<ssid>,<pass>` breaks on a comma in the password, `ADDWIFI ssid=..&pass=..`
// on an '&' in the SSID) and getting that wrong is a parse failure at the device
// with nothing visible at this end.
//
// ⚠️ THE LINK DROPS ON SUCCESS. The firmware saves the credentials and reboots
// into gateway mode within about 600 ms, often before its own notification is
// delivered. The caller — not this form — decides that a drop after the write is
// SUCCESS; see BleTestScreen.

import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, IconButton, InputAdornment, useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WifiIcon from '@mui/icons-material/Wifi';
import { chooseWifiPayload, MAX_PASS_LEN, MAX_SSID_LEN } from '../../../core/ble/bleConfig';

export default function BleWifiForm({ onSend, disabled, status }) {
  const theme = useTheme();
  const c = theme.sli;

  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');

  const submit = () => {
    // Validated BEFORE anything goes out: credentials neither form can carry are
    // a dead end, and finding that out from a device that silently never joins
    // costs a reboot and a walk back to the switch.
    const chosen = chooseWifiPayload(ssid, pass);
    if (!chosen.ok) { setError(chosen.error); return; }
    setError('');
    onSend(chosen.payload, ssid);
  };

  const field = {
    '& .MuiOutlinedInput-root': {
      color: c.text,
      fontSize: 13,
      '& fieldset': { borderColor: `${c.text}44` },
      '&:hover fieldset': { borderColor: `${c.text}77` },
    },
    '& .MuiInputLabel-root': { color: `${c.text}99`, fontSize: 13 },
  };

  return (
    <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 12 }}>
        Send Wi-Fi to this device
      </Typography>

      <TextField
        size="small"
        label="Network name (SSID)"
        value={ssid}
        onChange={(e) => setSsid(e.target.value)}
        inputProps={{ maxLength: MAX_SSID_LEN, autoCapitalize: 'none', autoCorrect: 'off' }}
        sx={field}
      />
      <TextField
        size="small"
        label="Password"
        type={show ? 'text' : 'password'}
        value={pass}
        onChange={(e) => setPass(e.target.value)}
        inputProps={{ maxLength: MAX_PASS_LEN, autoCapitalize: 'none', autoCorrect: 'off' }}
        sx={field}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {/* Shown on request: an SSID typed by hand is the commonest reason
                  provisioning "fails", and a hidden password hides the typo. */}
              <IconButton size="small" onClick={() => setShow((v) => !v)} sx={{ color: `${c.text}99` }}>
                {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        size="small"
        variant="contained"
        startIcon={<WifiIcon />}
        disabled={disabled || !ssid}
        onClick={submit}
        sx={{ bgcolor: c.button, color: '#fff', textTransform: 'none', borderRadius: '12px' }}
      >
        Send credentials
      </Button>

      {(error || status) && (
        <Typography sx={{ color: error ? '#EF5350' : `${c.text}99`, fontSize: 11 }}>
          {error || status}
        </Typography>
      )}
    </Box>
  );
}
